import { showNotification } from '@mantine/notifications';
import type { UserRole } from '@repo/common/enums/UserRole';
import { createMockableHook } from '@repo/common/utils/createMockableHook';
import type { QueryKey } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient, getEndpoints } from '../../common';
import queryClient from '../../queryClient';
import { useCurrentUser } from '../user/useCurrentUser';
import { WORKSPACE_NAMESPACE } from './constants';

// Constants for notification messages
const NOTIFICATION_MESSAGES = {
	SUCCESS: {
		title: 'Invitations sent successfully',
		message: 'Email invitations were sent to the specified users',
	},
	ERROR: {
		title: 'Error sending invitations',
		message: 'Failed to send invitations. Please try again.',
	},
} as const;

// HTTP status codes
const HTTP_STATUS = {
	UNPROCESSABLE_ENTITY: 422,
} as const;

/**
 * Interface for user invitation request data
 */
interface InviteUsersRequest {
	emails: string[];
	role: UserRole;
	iam_role_id?: string | null;
	groups: string[];
	teams: string[];
}

/**
 * Interface for API error response
 */
interface ApiErrorResponse {
	message: string;
}

/**
 * Hook to fetch the current workspace data
 * @returns Object containing the current workspace data
 */
export function useWorkspaceInternal() {
	const { data: user } = useCurrentUser();

	return {
		user,
		workspace: user?.workspace,
	};
}

export const [useWorkspace, MockUseWorkspace] =
	createMockableHook(useWorkspaceInternal);

/**
 * Hook to invite users to the workspace
 * @param invalidationKey - Query key to invalidate after successful invitation
 * @returns Mutation object for inviting users
 */
export function useInviteUsers(invalidationKey: QueryKey) {
	const { workspace } = useWorkspace();

	/**
	 * Handles the API call to invite users
	 * @param requestData - The invitation request data
	 * @returns Promise that resolves to true on success, false on failure
	 */
	const mutationFn = async (
		requestData: InviteUsersRequest
	): Promise<boolean> => {
		const { emails, role, groups, teams, iam_role_id } = requestData;

		if (!workspace?.id) {
			showNotification({
				title: NOTIFICATION_MESSAGES.ERROR.title,
				message: 'Workspace not found',
				color: 'red',
			});
			return false;
		}

		const url = getEndpoints(WORKSPACE_NAMESPACE).byPath([
			workspace.id,
			'invite',
		]);

		try {
			await apiClient.post<string>(url, {
				emails,
				role,
				groups,
				teams,
				iam_role_id,
			});

			showNotification({
				title: NOTIFICATION_MESSAGES.SUCCESS.title,
				message: NOTIFICATION_MESSAGES.SUCCESS.message,
			});
			return true;
		} catch (error: unknown) {
			handleInviteError(error);
			return false;
		}
	};

	/**
	 * Handles errors from the invite API call
	 * @param error - The caught error
	 */
	const handleInviteError = (error: unknown): void => {
		if (
			error instanceof AxiosError &&
			error.status === HTTP_STATUS.UNPROCESSABLE_ENTITY &&
			error.response?.data?.message
		) {
			const errorResponse = error.response.data as ApiErrorResponse;
			showNotification({
				title: NOTIFICATION_MESSAGES.ERROR.title,
				message: errorResponse.message,
				color: 'red',
			});
			return;
		}

		showNotification({
			title: NOTIFICATION_MESSAGES.ERROR.title,
			message: NOTIFICATION_MESSAGES.ERROR.message,
			color: 'red',
		});
	};

	return useMutation<boolean, unknown, InviteUsersRequest, unknown>({
		mutationFn,
		onSuccess() {
			queryClient.invalidateQueries({
				queryKey: invalidationKey,
			});
		},
	});
}
